import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

// Note: Ensure the logo image is in src/assets/logo_no_bg.png
import logo from '../assets/logo_no_bg.png';
import { useContact } from '../contexts/ContactContext';

const Navbar = () => {
  const { contactInfo } = useContact();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [location]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navClasses = `fixed top-0 w-full z-50 px-6 md:px-8 py-4 transition-all duration-300 ${scrolled ? 'bg-primary-container/90 border-b border-outline-variant/20 backdrop-blur-md' : 'bg-transparent dark:bg-transparent backdrop-blur-md'
    }`;

  const getNavLinkClass = ({ isActive }) =>
    `font-label-caps text-label-caps transition-all duration-300 ${isActive
      ? 'text-secondary border-b border-secondary pb-1 hover:text-primary'
      : 'text-on-surface hover:text-secondary'
    }`;

  const getDropdownLinkClass = ({ isActive }) =>
    `px-4 py-3 text-sm transition-colors border-l-2 ${isActive
      ? 'bg-surface-container text-secondary border-secondary'
      : 'border-transparent text-gray-200 hover:bg-surface-container hover:text-secondary hover:border-secondary/50'
    }`;

  const getMobileLinkClass = ({ isActive }) =>
    `px-4 py-3 text-sm font-label-caps transition-colors border-l-2 ${isActive
      ? 'bg-surface-container text-secondary border-secondary'
      : 'border-transparent text-on-surface hover:bg-surface-container hover:text-secondary'
    }`;

  return (
    <nav className={navClasses} id="main-nav">
      <div className="max-w-container-max mx-auto flex justify-between items-center h-16">

        {/* Left: Logo & Brand */}
        <Link to="/" className="flex items-center gap-4 cursor-pointer group">
          <div className="h-12 w-auto flex-shrink-0">
            <img
              src={logo}
              alt="HBB0011 REALTY Logo"
              className="h-full w-auto object-contain brightness-0 invert opacity-90 group-hover:opacity-100 transition-opacity"
            />
          </div>
          <div className="flex flex-col">
            <span className="font-headline-lg text-headline-lg-mobile md:text-headline-lg font-bold text-on-surface tracking-wider">HBB0011</span>
            <span className="font-label-caps text-[8px] md:text-[10px] text-on-surface-variant tracking-[0.3em] opacity-70">REALTY REALTORS PVT. LTD.</span>
          </div>
        </Link>

        {/* Center: Nav Links (Desktop) */}
        <div className="hidden lg:flex items-center gap-8">
          <NavLink to="/" className={getNavLinkClass}>HOME</NavLink>
          <NavLink to="/about" className={getNavLinkClass}>ABOUT US</NavLink>
          <NavLink to="/projects" className={getNavLinkClass}>PROJECTS</NavLink>
          <NavLink to="/services" className={getNavLinkClass}>SERVICES</NavLink>
          <NavLink to="/contact" className={getNavLinkClass}>CONTACT</NavLink>

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-1 font-label-caps text-label-caps transition-all duration-300 ${dropdownOpen ? 'text-secondary' : 'text-on-surface hover:text-secondary'}`}
            >
              MORE
              <span className={`material-symbols-outlined text-sm transition-transform duration-300 ${dropdownOpen ? 'rotate-180' : ''}`}>expand_more</span>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full right-0 mt-6 w-56 bg-primary-container border border-outline-variant/30 shadow-2xl rounded-md flex flex-col py-4 overflow-hidden z-50">
                <div className="flex flex-col">
                  <NavLink to="/signature-tower" onClick={() => setDropdownOpen(false)} className={getDropdownLinkClass}>Signature</NavLink>
                  <NavLink to="/our-presence" onClick={() => setDropdownOpen(false)} className={getDropdownLinkClass}>Presence</NavLink>
                  <NavLink to="/gallery" onClick={() => setDropdownOpen(false)} className={getDropdownLinkClass}>Gallery</NavLink>
                  <NavLink to="/testimonials" onClick={() => setDropdownOpen(false)} className={getDropdownLinkClass}>Testimonials</NavLink>
                  <NavLink to="/blog" onClick={() => setDropdownOpen(false)} className={getDropdownLinkClass}>Blog</NavLink>
                  <NavLink to="/faq" onClick={() => setDropdownOpen(false)} className={getDropdownLinkClass}>FAQ</NavLink>
                  <NavLink to="/investors" onClick={() => setDropdownOpen(false)} className={getDropdownLinkClass}>Investors</NavLink>
                  <NavLink to="/nri" onClick={() => setDropdownOpen(false)} className={getDropdownLinkClass}>NRI</NavLink>
                  <NavLink to="/careers" onClick={() => setDropdownOpen(false)} className={getDropdownLinkClass}>Careers</NavLink>
                  <NavLink to="/partner" onClick={() => setDropdownOpen(false)} className={getDropdownLinkClass}>Partner</NavLink>
                  <NavLink to="/book-visit" onClick={() => setDropdownOpen(false)} className={getDropdownLinkClass}>Book Visit</NavLink>
                  <NavLink to="/brochure" className={getMobileLinkClass}>Download Brochure</NavLink>
                  <NavLink to="/legal" className={getMobileLinkClass}>Legal & Docs</NavLink>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: Contact Info (Desktop) */}
        <div className="hidden xl:flex items-center gap-6 glass-panel px-6 py-2 rounded-full">
          <a href={`tel:${contactInfo?.phone?.replace(/[^0-9+]/g, '')}`} className="flex items-center gap-2 group">
            <span className="material-symbols-outlined text-[18px] text-secondary group-hover:scale-110 transition-transform">phone</span>
            <span className="font-label-caps text-[10px] text-on-surface group-hover:text-secondary transition-colors">{contactInfo?.phone}</span>
          </a>
          <div className="w-px h-4 bg-outline-variant"></div>
          <a href={`mailto:${contactInfo?.email}`} className="flex items-center gap-2 group">
            <span className="material-symbols-outlined text-[18px] text-secondary group-hover:scale-110 transition-transform">mail</span>
            <span className="font-label-caps text-[10px] text-on-surface group-hover:text-secondary transition-colors">{contactInfo?.email}</span>
          </a>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="lg:hidden text-on-surface p-2 focus:outline-none"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <span className="material-symbols-outlined">{mobileMenuOpen ? 'close' : 'menu'}</span>
        </button>

      </div>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 w-full bg-primary-container border-b border-outline-variant/20 p-4 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col space-y-1">
            <NavLink to="/" className={getMobileLinkClass}>HOME</NavLink>
            <NavLink to="/about" className={getMobileLinkClass}>ABOUT US</NavLink>
            <NavLink to="/projects" className={getMobileLinkClass}>PROJECTS</NavLink>
            <NavLink to="/services" className={getMobileLinkClass}>SERVICES</NavLink>
            <NavLink to="/contact" className={getMobileLinkClass}>CONTACT</NavLink>

            <div className="my-2 border-t border-outline-variant/20 pt-2 pb-1 px-4">
              <span className="text-xs font-label-caps text-on-surface-variant tracking-wider">MORE LINKS</span>
            </div>

            <NavLink to="/signature-tower" className={getMobileLinkClass}>Signature Tower</NavLink>
            <NavLink to="/our-presence" className={getMobileLinkClass}>Our Presence</NavLink>
            <NavLink to="/gallery" className={getMobileLinkClass}>Gallery</NavLink>
            <NavLink to="/testimonials" className={getMobileLinkClass}>Testimonials</NavLink>
            <NavLink to="/blog" className={getMobileLinkClass}>Blog & News</NavLink>
            <NavLink to="/faq" className={getMobileLinkClass}>FAQ</NavLink>
            <NavLink to="/investors" className={getMobileLinkClass}>Investors</NavLink>
            <NavLink to="/nri" className={getMobileLinkClass}>NRI Service</NavLink>
            <NavLink to="/careers" className={getMobileLinkClass}>Careers</NavLink>
            <NavLink to="/partner" className={getMobileLinkClass}>Partner With Us</NavLink>
            <NavLink to="/book-visit" className={getMobileLinkClass}>Book a Visit</NavLink>
            <NavLink to="/brochure" className={getMobileLinkClass}>Download Brochure</NavLink>
            <NavLink to="/legal" className={getMobileLinkClass}>Legal & Docs</NavLink>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
